/**
 * Embed Brain Page
 * Matches Hero Brain Card layout and functionality
 * Uses centralized theme system with inline styles for widget isolation
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type BrainCardData,
  type BrainQuickInfoResponse,
  type MyCognitionThreadsResponse,
  type StartCognitionThreadResponse,
} from '@/components/brain-card/types';
import {
  type CognitionChip,
  getAnswerText,
  type NextStepIntent,
  useCognitionAskStream,
} from '@/components/brain-card/useCognitionAskStream';

import { EmbedAccessGate } from '../components/EmbedAccessGate';
import { EmbedConversationContainer } from '../components/EmbedConversationContainer';
import { EmbedQuestionComposer } from '../components/EmbedQuestionComposer';
import { useEmbedSocket } from '../hooks/useEmbedSocket';
import { EmbedLoginPage } from '../pages/EmbedLoginPage';
import { useEmbedAuth } from '../providers/EmbedAuthProvider';
import { useOfferNewChat } from '../providers/EmbedChatActions';
import { useEmbedConfig } from '../providers/EmbedConfigProvider';
import { EmbedCognitionMessage, EmbedGuestMessageService } from '../services/EmbedGuestMessageService';
import { getWidgetHeaders, widgetProxyFetch } from '../services/widgetProxy';
import { useEmbedTheme } from '../theme';

// ============================================================================
// API Functions (using widget proxy)
// ============================================================================

async function fetchBrainData(
  appBaseUrl: string,
  brainId: string,
  token?: string | null,
): Promise<BrainCardData | null> {
  const result = await widgetProxyFetch<BrainQuickInfoResponse>(
    appBaseUrl,
    `/asset/cognition/quick-info/${brainId}`,
    { token },
  );
  return result?.data ?? null;
}

// Without this the embed would send a client-generated pseudo thread id
// to the SSE endpoint and the backend would respond with "Thread not found".
async function fetchMyCognitionThreads(
  appBaseUrl: string,
  cognitionId: string,
  token: string,
): Promise<{ _id: string }[]> {
  const result = await widgetProxyFetch<MyCognitionThreadsResponse>(
    appBaseUrl,
    `/asset/threads/my-cognition-threads?cognitionId=${cognitionId}&limit=10&offset=0`,
    { token },
  );
  return result?.data?.threads ?? [];
}

async function startCognitionThread(
  appBaseUrl: string,
  cognitionId: string,
  token: string,
): Promise<{ threadId: string } | null> {
  const result = await widgetProxyFetch<StartCognitionThreadResponse>(
    appBaseUrl,
    `/asset/threads/start-cognition-thread?cognitionId=${cognitionId}&streaming=true`,
    { token },
  );
  const threadId = result?.data?.thread?._id;
  return threadId ? { threadId } : null;
}

// Fetch the persisted message history for a thread. API returns DESC
// (newest first) with each USER message carrying its BOT response in a
// nested `replies[]`. The conversation container renders a flat list keyed
// by messageType, so we reverse to oldest-first and inline each reply
// right after its parent — otherwise bot turns silently disappear on reload.
async function fetchThreadMessages(
  appBaseUrl: string,
  threadId: string,
  token: string,
): Promise<EmbedCognitionMessage[]> {
  const result = await widgetProxyFetch<{
    data?: { messages?: EmbedCognitionMessage[] };
    messages?: EmbedCognitionMessage[];
  }>(appBaseUrl, `/asset/threads/get-cognition-messages/${threadId}?limit=20&offset=0&sort=DESC`, {
    token,
  });
  const raw = result?.data?.messages ?? result?.messages ?? [];
  const ascending = [...raw].reverse();
  const flat: EmbedCognitionMessage[] = [];
  for (const m of ascending) {
    const { replies, ...rest } = m as EmbedCognitionMessage & { replies?: EmbedCognitionMessage[] };
    flat.push(rest as EmbedCognitionMessage);
    if (Array.isArray(replies) && replies.length > 0) {
      const sortedReplies = [...replies].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      flat.push(...sortedReplies);
    }
  }
  return flat;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * About 75 characters a line at the widget's text size: answers and the
 * composer stop here on a wide page instead of running edge to edge.
 */
const READABLE_COLUMN: React.CSSProperties = { width: '100%', maxWidth: '48rem', margin: '0 auto' };

function appendMediaUrl(url: string | undefined, mediaBaseUrl: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${mediaBaseUrl}${url}`;
}

// ============================================================================
// Main Component
// ============================================================================

interface EmbedBrainPageProps {
  brainId: string;
}

export const EmbedBrainPage: React.FC<EmbedBrainPageProps> = ({ brainId }) => {
  const { appBaseUrl, mediaBaseUrl, assetApiUrl, guestMode, initialMessage, agents, conversationStarters } =
    useEmbedConfig();
  const { theme, primaryColor } = useEmbedTheme();
  const {
    isAuthenticated,
    isLoading: authLoading,
    guestToken,
    jwt,
    guestTokenLoading,
    user,
    logout,
  } = useEmbedAuth();

  const [brainData, setBrainData] = useState<BrainCardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<EmbedCognitionMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  // Bumped to re-check the brain (and its access verdict) after an unlock or a refused send.
  const [brainReloadKey, setBrainReloadKey] = useState(0);
  const [guestThreadId, setGuestThreadId] = useState<string | null>(null);
  const [authThreadId, setAuthThreadId] = useState<string | null>(null);
  const [chipsState, setChipsState] = useState<{
    chips: CognitionChip[];
    intent: NextStepIntent;
    questionText?: string;
  }>({ chips: [], intent: 'none' });
  const lastMessageIdRef = useRef<string | null>(null);
  // Set by "New chat": the next signed-in message starts a thread instead of reusing the latest.
  const startFreshThreadRef = useRef(false);

  const token = jwt || guestToken;
  const { socket, isConnected } = useEmbedSocket(token);

  const messageService = EmbedGuestMessageService.getInstance();
  const responseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingLoadingIdRef = useRef<string | null>(null);
  const initialMessageSentRef = useRef<boolean>(false);
  // Mirror messages into a ref so callbacks that only need to *read* the
  // current list (handleRetry traversing replyTo) don't have to subscribe to
  // every message update and re-create themselves on every streaming token.
  const messagesRef = useRef<EmbedCognitionMessage[]>(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // ── DSPY streaming hook (same one the brain page and SiteBrainChat use) ──
  // Embed widget passes its own `token` (jwt for signed-in visitors,
  // anonymous guestToken for everyone else, both issued by /api/tenant/jwt).
  // If DSPY accepts it, we stream replies inline instead of POSTing to
  // /threads/cognition-message-guest and waiting on Socket.IO. If it doesn't,
  // sendText throws and we fall back to the legacy path inside the catch.
  //
  // Route the SSE through the same-origin widget-proxy: a direct call to
  // dspy-api from an external embed site is cross-origin and CORS-blocked, so
  // the stream would never start and we'd silently drop to polling. The proxy
  // (`/api/widget-proxy/dspy/...`) keeps it same-origin and token-authed.
  const dspyProxyBaseUrl = appBaseUrl ? `${appBaseUrl}/api/widget-proxy/dspy` : undefined;
  const widgetRequestHeaders = useMemo(() => getWidgetHeaders(token ?? undefined), [token]);
  const { liveTurn, liveToolCall, completedTurn, sendText } = useCognitionAskStream({
    token: token ?? undefined,
    cognitionId: brainId,
    dspyBaseUrl: dspyProxyBaseUrl,
    requestHeaders: widgetRequestHeaders,
  });

  // turnId → loadingMessageId so the live/completed watchers can target the
  // exact bot-loading message we created during send.
  const pendingTurnRef = useRef<Map<string, string>>(new Map());

  // Live SSE deltas → mirror the running model text into the loading bot
  // message. First tick also cancels the 60s safety timeout — once tokens
  // are flowing we trust the stream and let it complete (or fail with a
  // CognitionStreamError) on its own.
  useEffect(() => {
    if (!liveTurn) return;
    const loadingMessageId = pendingTurnRef.current.get(liveTurn.id);
    if (!loadingMessageId) return;
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    const liveText = getAnswerText(liveTurn.rounds);
    setMessages((prev) =>
      prev.map((m) => (m._id === loadingMessageId ? { ...m, message: liveText, loading: true } : m)),
    );
  }, [liveTurn]);

  // Stream complete → finalize. Clear loading flag, drop the pending entry,
  // persist for guests so the message survives a remount.
  useEffect(() => {
    if (!completedTurn) return;
    const loadingMessageId = pendingTurnRef.current.get(completedTurn.id);
    if (!loadingMessageId) return;
    pendingTurnRef.current.delete(completedTurn.id);
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    pendingLoadingIdRef.current = null;
    const finalText = getAnswerText(completedTurn.rounds);
    setChipsState({
      chips: completedTurn.nextStepOptions ?? [],
      intent: completedTurn.nextStepIntent,
      questionText: completedTurn.clarifyingQuestionText,
    });
    setMessages((prev) => {
      const next = prev.map((m) =>
        m._id === loadingMessageId
          ? {
              ...m,
              message: finalText,
              loading: false,
              cognitionSuccess: true,
              sources: completedTurn.sources,
            }
          : m,
      );
      // Persist for guests — auth-side messages aren't stored here.
      if (!isAuthenticated && brainId) {
        const finalised = next.find((m) => m._id === loadingMessageId);
        if (finalised) messageService.addAIResponse(brainId, finalised, finalised.replyTo || '');
      }
      return next;
    });
    setIsSending(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: brainId/isAuthenticated/messageService stable per render
  }, [completedTurn]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleSignOut = useCallback(() => {
    logout();
    messageService.clearSession(brainId);
    setMessages([]);
    setAuthThreadId(null);
  }, [logout, messageService, brainId]);

  const handleNewChat = useCallback(() => {
    // Clear any pending response timeout
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    pendingLoadingIdRef.current = null;
    // A reply still streaming belongs to the old chat: drop it, so it neither
    // lands in the new one nor keeps the composer busy.
    pendingTurnRef.current.clear();
    setIsSending(false);
    setChipsState({ chips: [], intent: 'none' });

    messageService.clearSession(brainId);
    setMessages([]);
    setAuthThreadId(null);
    // Signed in, the next message would otherwise reuse the latest thread.
    startFreshThreadRef.current = true;
    lastMessageIdRef.current = null;
  }, [messageService, brainId]);

  const handleContinueAsGuest = useCallback(() => {
    setShowLoginModal(false);
  }, []);

  // ============================================================================
  // Effects
  // ============================================================================

  // Configure message service with asset API URL for direct calls
  // assetApiUrl may be empty on localhost, in which case widget-proxy is used as fallback
  useEffect(() => {
    if (appBaseUrl && token) {
      messageService.configure(appBaseUrl, token, assetApiUrl);
    }
  }, [appBaseUrl, token, assetApiUrl, messageService]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
      }
    };
  }, []);

  // Reset state when brainId changes
  useEffect(() => {
    // Clear any pending response timeout
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    pendingLoadingIdRef.current = null;
    lastMessageIdRef.current = null;

    // Reset all state
    setBrainData(null);
    setMessages([]);
    setError(null);
    setLoading(true);
    setGuestThreadId(null);
    setAuthThreadId(null);
    setChipsState({ chips: [], intent: 'none' });
  }, [brainId]);

  // Load messages and initialize thread with polling fallback (like main app)
  useEffect(() => {
    if (!brainId) return;

    // For guests, load from local storage and set up polling (like main app does)
    let messageCount = 0;
    let pollInterval = 100; // Start with fast polling
    let intervalId: NodeJS.Timeout;

    const loadGuestMessages = () => {
      const guestMessages = messageService.getMessages(brainId);

      // Only update state if messages actually changed
      if (guestMessages.length !== messageCount) {
        messageCount = guestMessages.length;
        setMessages(guestMessages);

        // Check if we got an AI response (non-loading BOT message)
        if (guestMessages.length > 0) {
          const lastMessage = guestMessages[guestMessages.length - 1];
          if (lastMessage.messageType === 'BOT' && !lastMessage.loading) {
            // Clear the response timeout since we got a response
            if (responseTimeoutRef.current) {
              clearTimeout(responseTimeoutRef.current);
              responseTimeoutRef.current = null;
            }
            pendingLoadingIdRef.current = null;
          }
        }

        // Reset to fast polling when new message detected
        pollInterval = 100;
        clearInterval(intervalId);
        intervalId = setInterval(loadGuestMessages, pollInterval);
      } else if (pollInterval < 2000) {
        // Gradually slow down polling if no changes
        pollInterval = Math.min(pollInterval * 1.5, 2000);
        clearInterval(intervalId);
        intervalId = setInterval(loadGuestMessages, pollInterval);
      }
    };

    loadGuestMessages();

    // Get or create guest session
    const session = messageService.getOrCreateSession(brainId);
    setGuestThreadId(session.threadId);

    // Start polling with adaptive interval
    intervalId = setInterval(loadGuestMessages, pollInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [brainId, messageService]);

  // Socket handling
  useEffect(() => {
    const activeThreadId = isAuthenticated ? authThreadId : guestThreadId;

    if (!socket || !activeThreadId || !brainId) {
      return;
    }

    if (isConnected) {
      socket.emit('thread:join', activeThreadId);
    } else if (!socket.connected) {
      socket.connect();
    }

    const handleNewMessage = (message: any) => {
      const incomingThreadId = message?.myCognitionThread?._id || message?.threadId;
      if (incomingThreadId && incomingThreadId !== activeThreadId) {
        return;
      }

      // DSPY SSE is the primary delivery channel. While a turn is streaming
      // (or has just completed and its watcher is still draining), suppress
      // the legacy socket path so we don't append a duplicate bot bubble.
      if (pendingTurnRef.current.size > 0) {
        return;
      }

      if (message.messageType === 'BOT' && !message.loading) {
        // Clear the response timeout since we got a response
        if (responseTimeoutRef.current) {
          clearTimeout(responseTimeoutRef.current);
          responseTimeoutRef.current = null;
        }
        pendingLoadingIdRef.current = null;

        // Extract message text - check message field first, then contents array
        let messageText = message.message || '';
        if (!messageText && message.contents && message.contents.length > 0) {
          // Extract text from contents array (format: contents[].content.text)
          const textContents = message.contents
            .filter((c: any) => c.contentType === 'text' && c.content?.text)
            .map((c: any) => c.content.text);
          messageText = textContents.join('\n');
        }

        const aiMessage: EmbedCognitionMessage = {
          _id: message._id,
          message: messageText,
          messageType: 'BOT',
          loading: false,
          contents: message.contents || [],
          createdBy: message.createdBy || {
            Id: 'ai',
            Name: brainData?.cognition.name || 'AI',
            DisplayPic: '',
          },
          createdAt: message.createdAt || new Date().toISOString(),
          read: true,
          replyTo: message.replyTo || lastMessageIdRef.current,
          thinking: message.thinking,
          cognitionSuccess: message.cognitionSuccess !== false,
          myCognitionThread: { _id: activeThreadId },
          cognition: message.cognition || { _id: brainId, name: brainData?.cognition.name || 'AI' },
        };

        if (isAuthenticated) {
          setMessages((prev) => {
            const loadingIndex = prev.findIndex((m) => m.loading && m.messageType === 'BOT');
            if (loadingIndex >= 0) {
              const updated = [...prev];
              updated[loadingIndex] = aiMessage;
              return updated;
            }
            return [...prev, aiMessage];
          });
        } else {
          messageService.addAIResponse(brainId, aiMessage, aiMessage.replyTo || '');
          setMessages(messageService.getMessages(brainId));
        }
      }
    };

    socket.on('message:new', handleNewMessage);

    return () => {
      socket.off('message:new', handleNewMessage);
    };
  }, [socket, isConnected, guestThreadId, authThreadId, brainId, brainData, messageService, isAuthenticated]);

  // Close login modal on auth
  useEffect(() => {
    if (isAuthenticated && showLoginModal) {
      setShowLoginModal(false);
    }
  }, [isAuthenticated, showLoginModal]);

  // Fetch brain data
  useEffect(() => {
    if (!brainId || !appBaseUrl || guestTokenLoading || !token) return;

    setLoading(true);
    fetchBrainData(appBaseUrl, brainId, token)
      .then((data) => {
        if (data) {
          setBrainData(data);
        } else {
          setError('Brain not found');
        }
      })
      .catch((err) => {
        setError(err.message || 'Failed to load brain');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [brainId, appBaseUrl, token, guestTokenLoading, brainReloadKey]);

  // The brain's "Who can chat" verdict for this visitor (server-computed).
  const askLocked = brainData?.cognition.canAsk === false;
  const askDeniedReason = brainData?.cognition.askAccessDeniedReason;
  const askAccessMode = brainData?.cognition.askAccessMode;

  // Pre-hydrate the auth thread from the backend. Without this the embed
  // either fell through to the guest pseudo-id branch (→ "Thread not found")
  // or created a brand-new thread on every send, ignoring the user's
  // existing 10+ threads for this cognition. authLoading guard avoids the
  // double-fire while `isAuthenticated` flips before `jwt` lands.
  useEffect(() => {
    if (authLoading || !isAuthenticated || !jwt || !brainId || !appBaseUrl) return;
    if (authThreadId) return;
    let cancelled = false;
    fetchMyCognitionThreads(appBaseUrl, brainId, jwt).then((threads) => {
      if (cancelled) return;
      if (threads.length > 0) {
        setAuthThreadId(threads[0]._id);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, jwt, brainId, appBaseUrl, authThreadId]);

  // Hydrate the conversation from persisted messages the first time we
  // resolve a thread (page refresh / cognition switch). The guard is only
  // flipped on success so a transient failure can retry when deps change
  // (e.g. jwt rotation). When optimistic state has already been pushed for
  // a fast send, we merge by _id rather than dropping the history.
  const historyLoadedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!authThreadId || !jwt || !appBaseUrl) return;
    if (historyLoadedRef.current === authThreadId) return;
    let cancelled = false;
    fetchThreadMessages(appBaseUrl, authThreadId, jwt)
      .then((history) => {
        if (cancelled) return;
        historyLoadedRef.current = authThreadId;
        if (history.length === 0) return;
        setMessages((prev) => {
          if (prev.length === 0) return history;
          const seen = new Set(prev.map((m) => m._id));
          const older = history.filter((m) => !seen.has(m._id));
          return older.length === 0 ? prev : [...older, ...prev];
        });
      })
      .catch(() => {
        // Leave the ref unset so dep changes (jwt refresh, etc.) can retry.
      });
    return () => {
      cancelled = true;
    };
  }, [authThreadId, jwt, appBaseUrl]);

  // ============================================================================
  // Message Handling
  // ============================================================================

  const handleMessagesUpdate = useCallback((updatedMessages: EmbedCognitionMessage[]) => {
    setMessages(updatedMessages);
  }, []);

  const handleSendMessage = useCallback(
    async (message: string, options?: { forceWebSearch?: boolean }) => {
      if (!brainData || !brainId || isSending || !appBaseUrl || askLocked) return;
      // Auth resolution can race the first send. If we picked the guest
      // branch while jwt is still being hydrated, we'd send a client-minted
      // pseudo thread id to the SSE endpoint → "Thread not found".
      if (authLoading) return;

      // Clear stale chips from prior turn — they refer to a question that's no longer the current one.
      setChipsState({ chips: [], intent: 'none' });
      setIsSending(true);
      try {
        const userId = user?.id || 'guest';
        const userName = user?.fullName || user?.email || 'You';
        const cognitionName = brainData.cognition.name;

        const userMessageId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const loadingMessageId = `${Date.now()}-loading-${Math.random().toString(36).substr(2, 6)}`;

        // Push the optimistic user + loading bubbles BEFORE resolving the
        // thread or opening the SSE stream — resolution can take 200-800ms
        // and the user shouldn't be staring at an empty conversation while
        // we negotiate. The thread id is metadata that doesn't affect the
        // bubble render, so we patch it once resolved.
        const userMessage: EmbedCognitionMessage = {
          _id: userMessageId,
          message,
          messageType: 'USER',
          contents: [],
          createdBy: { Id: userId, Name: userName, DisplayPic: user?.displayPic || '' },
          createdAt: new Date().toISOString(),
          read: true,
          myCognitionThread: { _id: '' },
        };
        const loadingMessage: EmbedCognitionMessage = {
          _id: loadingMessageId,
          message: '',
          messageType: 'BOT',
          loading: true,
          contents: [],
          createdBy: { Id: 'ai', Name: cognitionName, DisplayPic: '' },
          createdAt: new Date().toISOString(),
          read: false,
          replyTo: userMessageId,
          myCognitionThread: { _id: '' },
          cognition: { _id: brainId, name: cognitionName },
        };
        setMessages((prev) => [...prev, userMessage, loadingMessage]);
        lastMessageIdRef.current = userMessageId;
        pendingLoadingIdRef.current = loadingMessageId;

        // Resolve the thread to use:
        //  - Authenticated:  reuse the user's most recent thread (pre-fetched
        //                    via fetchMyCognitionThreads), creating one only
        //                    when the user has none for this cognition.
        //  - Guest:          pseudo thread id from EmbedGuestMessageService.
        let threadId: string;
        if (isAuthenticated && jwt) {
          let tid = authThreadId;
          if (!tid) {
            const existing = startFreshThreadRef.current
              ? []
              : await fetchMyCognitionThreads(appBaseUrl, brainId, jwt);
            startFreshThreadRef.current = false;
            if (existing.length > 0) {
              tid = existing[0]._id;
            } else {
              const threadResult = await startCognitionThread(appBaseUrl, brainId, jwt);
              if (!threadResult?.threadId) throw new Error('Failed to create thread');
              tid = threadResult.threadId;
            }
            setAuthThreadId(tid);
          }
          threadId = tid;
        } else {
          const currentSession = messageService.getOrCreateSession(brainId);
          threadId = currentSession.threadId;
          if (currentSession.threadId !== guestThreadId) {
            setGuestThreadId(currentSession.threadId);
          }
        }

        // Patch the optimistic pair with the resolved thread id so socket
        // filtering and guest-persisted state stay consistent.
        setMessages((prev) =>
          prev.map((m) =>
            m._id === userMessageId || m._id === loadingMessageId
              ? { ...m, myCognitionThread: { _id: threadId } }
              : m,
          ),
        );

        // Persist the optimistic pair for guests so a refresh doesn't lose
        // the in-flight state (the loading message will get finalized by the
        // completedTurn watcher above).
        if (!isAuthenticated) {
          const session = messageService.getOrCreateSession(brainId);
          session.messages.push(
            { ...userMessage, myCognitionThread: { _id: threadId } },
            { ...loadingMessage, myCognitionThread: { _id: threadId } },
          );
        }

        // Stream the reply via DSPY. The completedTurn watcher finalizes the
        // loading message with the streamed text and clears isSending. If
        // sendText throws (no DSPY URL configured, CORS, token rejected),
        // we fall through to the catch below and show an error.
        const turnId = await sendText(
          threadId,
          message,
          options?.forceWebSearch ? { forceWebSearch: true } : undefined,
        );
        if (turnId) pendingTurnRef.current.set(turnId, loadingMessageId);

        // Safety timeout — if neither liveTurn nor completedTurn fire within
        // 60s (e.g. silent stream stall), surface an error in the loading
        // message and unblock the composer.
        responseTimeoutRef.current = setTimeout(() => {
          if (!pendingTurnRef.current.has(turnId)) return;
          pendingTurnRef.current.delete(turnId);
          setMessages((prev) =>
            prev.map((m) =>
              m._id === loadingMessageId
                ? {
                    ...m,
                    message: "Sorry, I couldn't get a response. Please try again.",
                    loading: false,
                    cognitionSuccess: false,
                  }
                : m,
            ),
          );
          pendingLoadingIdRef.current = null;
          setIsSending(false);
        }, 60000);
      } catch (error) {
        console.error('[EmbedBrain] Failed to send message:', error);
        // A 403 means the brain's access setting refused this visitor (the
        // proxy's ask-access gate): say so, and re-check the brain so the
        // composer turns into the matching sign-in / code / restricted panel.
        const refused = (error as { httpStatus?: number } | null)?.httpStatus === 403;
        if (refused) setBrainReloadKey((n) => n + 1);
        // Flip the loading bubble into an error so the retry button can
        // surface — the user message ref still points at the prompt that
        // failed, so the conversation tree stays intact.
        setMessages((prev) =>
          prev.map((m) =>
            m.loading
              ? {
                  ...m,
                  message: refused
                    ? "You don't have access to chat with this assistant."
                    : "Sorry, I couldn't get a response. Please try again.",
                  loading: false,
                  cognitionSuccess: false,
                }
              : m,
          ),
        );
        pendingLoadingIdRef.current = null;
        setIsSending(false);
      }
    },
    [
      askLocked,
      brainData,
      brainId,
      user,
      messageService,
      isSending,
      isAuthenticated,
      authLoading,
      jwt,
      appBaseUrl,
      authThreadId,
      guestThreadId,
      sendText,
    ],
  );

  const handlePromptClick = useCallback(
    (prompt: string) => {
      handleSendMessage(prompt);
    },
    [handleSendMessage],
  );

  // Retry an errored bot reply by resubmitting the user's original question.
  // Trace failed BOT → replyTo → USER bubble; drop both so the resend
  // re-creates clean optimistic state. Reads via messagesRef so the callback
  // identity stays stable through streaming.
  const handleRetry = useCallback(
    (failed: EmbedCognitionMessage) => {
      const userMessage = messagesRef.current.find((m) => m._id === failed.replyTo);
      const text = userMessage?.message;
      if (!text) return;
      setMessages((prev) => prev.filter((m) => m._id !== failed._id && m._id !== failed.replyTo));
      handleSendMessage(text);
    },
    [handleSendMessage],
  );

  // Send initial message if provided (for activating chat from demo UI)
  useEffect(() => {
    if (initialMessage && !initialMessageSentRef.current && brainData && !loading && !isSending && token) {
      initialMessageSentRef.current = true;
      handleSendMessage(initialMessage);
    }
  }, [initialMessage, brainData, loading, isSending, token, handleSendMessage]);

  // ============================================================================
  // Render
  // ============================================================================

  const isLoading = loading || guestTokenLoading || !token;
  const hasError = !isLoading && (error || !brainData);
  const hasMessages = messages.length > 0;
  // The header's "New chat" — offered once there is a conversation to leave.
  useOfferNewChat(hasMessages ? handleNewChat : null);
  const brainImage = brainData?.cognition.displayImage
    ? appendMediaUrl(brainData.cognition.displayImage, mediaBaseUrl)
    : undefined;
  const vendor = brainData?.cognition?.vendor;
  const owner = brainData?.userDetails;

  const creatorName = vendor?.businessName || owner?.Name;
  const creatorImage = vendor
    ? appendMediaUrl(vendor.businessLogo || vendor.businessProfileImage, mediaBaseUrl)
    : appendMediaUrl(owner?.DisplayPic, mediaBaseUrl);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        backgroundColor: theme.colors.background.subtle,
        overflow: 'hidden',
      }}
    >
      {/* Loading state */}
      {isLoading && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.colors.background.subtle,
            minHeight: '300px',
          }}
        >
          <div
            style={{
              width: '32px',
              height: '32px',
              border: `3px solid ${theme.colors.border.subtle}`,
              borderTopColor: primaryColor,
              borderRadius: '50%',
              animation: 'embed-spin 0.8s linear infinite',
            }}
          />
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            padding: theme.spacing['6'],
            backgroundColor: theme.colors.background.subtle,
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: theme.colors.background.hover,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: theme.spacing['4'],
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke={theme.colors.text.secondary}
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
            {error || 'Brain not found'}
          </p>
        </div>
      )}

      {/* Brain Info Card (hidden when conversation has messages, loading/error, or agents configured) */}
      {!isLoading && !hasError && brainData && !hasMessages && (!agents || agents.length === 0) && (
        <div
          style={{
            padding: '12px 12px 8px',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              backgroundColor: theme.colors.background.surface,
              borderRadius: theme.borderRadius.lg,
              padding: '12px',
              boxShadow: theme.shadow.sm,
              border: `${theme.borderWidth.thin} solid ${theme.colors.border.subtle}`,
            }}
          >
            {/* Avatar + Content */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Brain Avatar - Compact */}
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: theme.borderRadius.md,
                  overflow: 'hidden',
                  flexShrink: 0,
                  background: brainImage
                    ? undefined
                    : `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {brainImage ? (
                  <img
                    src={brainImage}
                    alt={brainData.cognition.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="1.5"
                  >
                    <path d="M12 2a9 9 0 0 0-9 9c0 3.6 2.4 6.6 5.7 7.7.3.1.5-.1.5-.4v-1.5c-2.3.5-2.8-1.1-2.8-1.1-.4-1-.9-1.3-.9-1.3-.7-.5.1-.5.1-.5.8.1 1.2.8 1.2.8.7 1.2 1.9.9 2.3.7.1-.5.3-.9.5-1.1-1.8-.2-3.7-.9-3.7-4 0-.9.3-1.6.8-2.2-.1-.2-.4-1 .1-2.1 0 0 .7-.2 2.2.8.6-.2 1.3-.3 2-.3s1.4.1 2 .3c1.5-1 2.2-.8 2.2-.8.4 1.1.2 1.9.1 2.1.5.6.8 1.3.8 2.2 0 3.1-1.9 3.8-3.7 4 .3.3.6.8.6 1.6v2.4c0 .3.2.5.5.4A9 9 0 0 0 12 2z" />
                  </svg>
                )}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Brain Name */}
                <h1
                  style={{
                    fontSize: theme.typography.fontSize.base,
                    fontWeight: theme.typography.fontWeight.semibold,
                    color: theme.colors.text.primary,
                    margin: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {brainData.cognition.name}
                </h1>

                {/* Creator Attribution - inline */}
                {creatorName && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      marginTop: '2px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.text.tertiary,
                      }}
                    >
                      by {creatorName}
                    </span>
                    {creatorImage && (
                      <img
                        src={creatorImage}
                        alt={creatorName}
                        style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: theme.borderRadius.sm,
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Description - below, truncated */}
            {brainData.cognition.description && (
              <p
                style={{
                  fontSize: theme.typography.fontSize.xs,
                  color: theme.colors.text.secondary,
                  margin: '8px 0 0 0',
                  lineHeight: theme.typography.lineHeight.relaxed,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {brainData.cognition.description}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Main Card - Conversation Area (only show when loaded successfully) */}
      {!isLoading && !hasError && brainData && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.colors.background.surface,
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          {/* Messages or Launch Prompts. On a wide page (inline / full page) the
              column stops at a readable line length instead of running edge to edge. */}
          <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            <div style={READABLE_COLUMN}>
              {hasMessages ? (
                <EmbedConversationContainer
                  messages={messages}
                  mediaBaseUrl={mediaBaseUrl}
                  cognitionName={brainData.cognition.name}
                  cognitionImage={brainData.cognition.displayImage}
                  chips={chipsState.chips}
                  chipsIntent={chipsState.intent}
                  clarifyingQuestionText={chipsState.questionText}
                  onChipClick={handlePromptClick}
                  liveToolLabel={liveToolCall?.label}
                  onRetry={handleRetry}
                />
              ) : (
                /* Conversation starters — FloatingAssistant-style accent pills.
                 Custom (widget-configured) starters override the brain's launch prompts. */
                <div style={{ padding: '12px' }}>
                  {(() => {
                    const customStarters = (conversationStarters ?? []).map((s) => s.trim()).filter(Boolean);
                    const starters =
                      customStarters.length > 0 ? customStarters : brainData.cognition.launchPrompts;
                    return !askLocked && starters && starters.length > 0 ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                        }}
                      >
                        {starters.slice(0, 4).map((prompt, index) => (
                          <button
                            key={index}
                            onClick={() => handlePromptClick(prompt)}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '8px',
                              width: '100%',
                              padding: '10px 14px',
                              backgroundColor: `${primaryColor}10`,
                              border: `1px solid ${primaryColor}40`,
                              borderRadius: '12px',
                              fontSize: theme.typography.fontSize.sm,
                              fontWeight: theme.typography.fontWeight.medium,
                              color: theme.colors.text.primary,
                              textAlign: 'left',
                              lineHeight: 1.4,
                              cursor: 'pointer',
                              transition: `all ${theme.animation.duration.fast}`,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = `${primaryColor}1f`;
                              e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = `${primaryColor}10`;
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke={primaryColor}
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{ opacity: 0.6, flexShrink: 0, marginTop: '3px' }}
                            >
                              <path d="M12 3l1.9 5.8L19.5 10l-5.6 1.2L12 17l-1.9-5.8L4.5 10l5.6-1.2L12 3z" />
                            </svg>
                            <span style={{ flex: 1, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {prompt}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Question Composer — or, when the brain's access setting doesn't admit
              this visitor, the matching sign-in / code / restricted panel. */}
          {askLocked ? (
            <EmbedAccessGate
              brainId={brainId}
              brainName={brainData.cognition.name}
              reason={askDeniedReason}
              mode={askAccessMode}
              guestMode={!!guestMode}
              appBaseUrl={appBaseUrl || ''}
              token={token}
              onSignIn={() => setShowLoginModal(true)}
              onUnlocked={() => setBrainReloadKey((n) => n + 1)}
            />
          ) : (
            <div
              style={{
                borderTop: `${theme.borderWidth.thin} solid ${theme.colors.border.subtle}`,
                flexShrink: 0,
              }}
            >
              <div style={READABLE_COLUMN}>
                <EmbedQuestionComposer
                  onSend={handleSendMessage}
                  placeholder={`Ask ${brainData.cognition.name}...`}
                  isLoading={isSending}
                  showWebSearchToggle
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Login Modal (disabled in guest mode for security) */}
      {!guestMode && showLoginModal && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowLoginModal(false);
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              maxWidth: '400px',
              maxHeight: '600px',
              backgroundColor: theme.colors.background.page,
              borderRadius: theme.borderRadius.xl,
              overflow: 'auto',
              position: 'relative',
              boxShadow: theme.shadow.xl,
            }}
          >
            <button
              onClick={() => setShowLoginModal(false)}
              style={{
                position: 'absolute',
                top: theme.spacing['3'],
                right: theme.spacing['3'],
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.background.hover,
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                zIndex: 10,
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke={theme.colors.text.secondary}
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <EmbedLoginPage onContinueAsGuest={handleContinueAsGuest} />
          </div>
        </div>
      )}
    </div>
  );
};

export default EmbedBrainPage;
